import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useCollectionApi from "hooks/api/private/useCollectionApi";
import useSnackbar from "hooks/useSnackbar";

import { CollectionListItem } from "types/api/collection";

export type CollectionListHook = {
    navigateToNewCollection: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dataTableHook: DataTableHook<CollectionListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useCollectionList(): CollectionListHook {
    const navigate = useNavigate();

    const { getAllCollection, deleteCollection } = useCollectionApi();

    const onEditClick = React.useCallback(
        (row: CollectionListItem) => {
            navigate(
                makeUrl("/gestione-prodotti/collezioni/:collectionId/modifica", {
                    collectionId: row.id,
                }),
            );
        },
        [navigate],
    );

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "collection-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.name = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const { filters } = filterSearchHook;

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Collezione",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteCollectionButton = React.useCallback(
        (row: CollectionListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la collezione?", () => {
                const [apiCall] = deleteCollection({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Collezione eliminata con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione la collezione"));
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteCollection,
        ],
    );

    const columns: Array<Column<CollectionListItem>> = React.useMemo(() => {
        return [
            {
                id: "name",
                header: "Denominazione",
                render: (row: CollectionListItem) => row.name,
                orderable: true,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: CollectionListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={onEditClick}
                        onDelete={deleteCollectionButton}
                    />
                ),
            },
        ];
    }, [onEditClick, deleteCollectionButton]);

    const dataTableHook = useDataTableWithMemory({
        apiCall: getAllCollection,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "collection-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewCollection = React.useCallback(() => {
        navigate("/gestione-prodotti/collezioni/nuova");
    }, [navigate]);

    return React.useMemo(
        (): CollectionListHook => ({
            navigateToNewCollection,
            onSearchFilterChange,
            filterSearchHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            navigateToNewCollection,
            onSearchFilterChange,
            filterSearchHook,
            alertConfirmHook,
            dataTableHook,
        ],
    );
}

export default useCollectionList;
